import React, { useRef, useState, useEffect } from 'react';
import cx from 'classnames';
import { isMobile } from 'react-device-detect';

import { DefaultFilledButton, PrimaryButton } from '@magoosh/lib/button';
import { FontAwesomeIcon } from '@magoosh/lib/icons';
import { outputDeviceId } from '@magoosh/organisms/audio/audio_devices';
import { loadVolume, persistVolume } from '@magoosh/organisms/audio/volume';
import VideoPlayer from '@magoosh/organisms/video_player';

import StimulusControlsAndSettings, { Settings } from '../components/stimulus_controls_and_settings';

import style from './style.module.scss';

interface Props {
  compatibilityMode: boolean;
  direction?: string;
  isAnswered: boolean;
  onChangeCompatibilityMode?: (compatibilityMode: boolean) => void;
  onChangeVolume?: (volume: number) => void;
  onFinishPlayback?: () => void;
  onResetPlayback?: (compatibilityMode: boolean) => void;
  playButtonText?: string;
  posterSrc?: string;
  referenceImageSrc: string;
  src: string;
}

export default (props: Props) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [autoplayFailed, setAutoplayFailed] = useState<boolean>(false);
  const [compatibilityMode, setCompatabilityMode] = useState<boolean>(props.compatibilityMode);
  const [recordingHasStarted, setRecordingHasStarted] = useState<boolean>(false);
  const [videoHasFinished, setVideoHasFinished] = useState<boolean>(false);
  const [videoHasStarted, setVideoHasStarted] = useState<boolean>(false);
  const [volume, setVolume] = useState<number>(1);

  useEffect(() => {
    videoRef.current = document.querySelector('#video-player-stimulus video.vjs-tech');

    if (props.isAnswered) return;
    if (compatibilityMode) return;

    attemptAutoplay();
  }, []);

  useEffect(() => {
    videoRef.current = document.querySelector('#video-player-stimulus video.vjs-tech');

    const savedVolume = loadVolume();
    setVolume(savedVolume);

    if (!videoRef.current) return;

    const handleVolumeChange = () => {
      const newVolume = videoRef.current.muted ? 0 : videoRef.current.volume;
      setVolume(newVolume);
      persistVolume(newVolume);
      props.onChangeVolume?.(newVolume);
    };

    const handlePlay = () => {
      if (videoRef.current && 'setSinkId' in videoRef.current) {
        (videoRef.current as any).setSinkId(outputDeviceId()).catch(() => {});
      }
    };

    // Defer volume application and listener attachment until after VideoJS finishes
    // initializing, otherwise its volume reset to 1.0 overwrites our saved value.
    const timeout = setTimeout(() => {
      if (!videoRef.current) return;
      videoRef.current.volume = savedVolume;
      videoRef.current.addEventListener('volumechange', handleVolumeChange);
    }, 100);

    videoRef.current.addEventListener('play', handlePlay);

    return () => {
      clearTimeout(timeout);
      videoRef.current?.removeEventListener('volumechange', handleVolumeChange);
      videoRef.current?.removeEventListener('play', handlePlay);
    };
  }, []);

  // Listen for the audio recorder to indicate that recording started. When this happens we hide
  // the video player and show the reference image.
  useEffect(() => {
    const handleRecordingHasStarted = (event) => {
      setRecordingHasStarted(true);
    };

    document.addEventListener('recordingStarted', handleRecordingHasStarted);

    return () => {
      document.removeEventListener('recordingStarted', handleRecordingHasStarted);
    };
  }, []);

  const attemptAutoplay = () => {
    if (!videoRef.current) return;

    if (isMobile) {
      setAutoplayFailed(true);
      return;
    }

    setAutoplayFailed(false);
      
    // Wait to start playback to not be so sudden
    setTimeout(() => {
      setVideoHasStarted(true);

      videoRef.current.play().catch(() => {
        setAutoplayFailed(true);
        setVideoHasStarted(false);
      });
    }, 1000);
  };

  const handleSettingsChanged = (settings: Settings) => {
    setCompatabilityMode(settings.compatibilityMode);
    props.onChangeCompatibilityMode?.(settings.compatibilityMode);

    resetQuestion(settings.compatibilityMode);    

    if (settings.compatibilityMode || props.isAnswered) {
      document.querySelector('#video-player-stimulus').classList.remove('vjs-controls-disabled');
    } else {
      document.querySelector('#video-player-stimulus').classList.add('vjs-controls-disabled');
    }

    if (!props.isAnswered && !settings.compatibilityMode) {
      attemptAutoplay();
    }
  };

  const handleVolumeChanged = (newValue: number) => {
    setVolume(newValue);
    persistVolume(newValue);
    props.onChangeVolume?.(newValue);
    videoRef.current.volume = newValue;
  };

  const resetQuestion = (compatibilityMode: boolean) => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }

    props.onResetPlayback?.(compatibilityMode);
    setAutoplayFailed(true);
    setVideoHasFinished(false);
    setVideoHasStarted(false);
    setRecordingHasStarted(false);
  };

  const ButtonComponent = autoplayFailed ? PrimaryButton : DefaultFilledButton;

  return (
    <>
      <div className={cx(style.videoStimulus, 'align-items-center d-flex flex-column gap-m')}>
        <StimulusControlsAndSettings
          compatibilityMode={compatibilityMode}
          onChangeVolume={handleVolumeChanged}
          onSave={handleSettingsChanged}
          onShowSettings={() => resetQuestion(compatibilityMode)}
          volume={volume}
        />

        {
          props.direction && (
            <div className="h4 text-bold text-center u-margin-A-n">
              {props.direction}
            </div>
          )
        }

        <div style={{ maxWidth: '100%' }}>
          <img
            alt=""
            src={props.referenceImageSrc}
            style={{ display: recordingHasStarted ? 'block' : 'none' }}
          />

          <div style={{ display: recordingHasStarted ? 'none' : 'block' }}>
            {/*@ts-ignore*/}
            <VideoPlayer
              fallbackFile={props.src}
              noViewTracking
              onEnd={() => {
                if(!props.isAnswered && !compatibilityMode) {
                  setVideoHasFinished(true);
                  props.onFinishPlayback?.();
                }
              }}
              options={{
                controls: props.isAnswered || compatibilityMode,
                poster: props.posterSrc,
                sources: [{ src: props.src }]
              }}
              videoFileId="stimulus"
            />
          </div>
        </div>
    
        {
          !compatibilityMode && !props.isAnswered && (
           <ButtonComponent
              disabled={videoHasStarted || !autoplayFailed}
              onClick={() => {
                setVideoHasStarted(true);
                videoRef.current.play().catch(() => { setVideoHasStarted(false) });
              }}
            >
              {(() => {
                if (videoHasFinished) {
                  return 'Video Has Finished Playing';
                } else if (videoHasStarted) {
                  return 'Video Is Playing';
                } else {
                  return props.playButtonText || 'Play Video';
                }
              })()}
            </ButtonComponent>
          )
        }

        {
          compatibilityMode && (
            <div className="small text-center">
              On the actual exam, the video will play automatically and can only be listened to once.
            </div>
          )
        }
      </div>
    </>
  );
};
